import { ActionType, ProTable } from "@ant-design/pro-components";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Form, message, Modal, Popconfirm, Select, Switch } from "antd";
import { useEffect, useRef } from "react";
import GlobalWrap from "../../components/globalWrap";
import { useGlobalState } from "../../components/global";
import HealthConsultationConfigForm from "./HealthConsultationConfigForm";

export default function HealthConsultationConfig() {
    const { jsonRequest } = useGlobalState();
    const tableRef = useRef<ActionType>();
    const [form] = Form.useForm();

    useEffect(() => {
        tableRef.current?.reload();
    }, []);

    const request = async (params: any) => {
        const result: any = await jsonRequest.get("/back/health-consultation-config/page", {
            currentPage: params.current,
            pageSize: params.pageSize,
            channel: params.channel,
            enable: params.enable,
        }).then((resp: any) => resp.json());
        const data = result?.data;
        return {
            data: data?.data || [],
            success: result?.code === 0,
            total: data?.count || 0,
        };
    };

    const saveConfig = async (item: any) => {
        const values = await form.validateFields();
        const request = item.id
            ? jsonRequest.put("/back/health-consultation-config/modify", { ...values, id: item.id })
            : jsonRequest.post("/back/health-consultation-config/add", values);
        const result: any = await request.then((resp: any) => resp.json());
        if (result?.code !== 0) {
            message.error(result?.msg || "保存失败");
            throw new Error(result?.msg || "保存失败");
        }
        message.success(item.id ? "保存成功" : "新增成功");
        tableRef.current?.reload();
        form.resetFields();
    };

    const openConfigModal = (item: any) => {
        Modal.confirm({
            width: 760,
            title: item.id ? `编辑健康咨询配置 - ${item.channel}` : "新增健康咨询配置",
            content: (
                <GlobalWrap>
                    <HealthConsultationConfigForm form={form} item={item} />
                </GlobalWrap>
            ),
            onOk: () => saveConfig(item),
            onCancel: () => form.resetFields(),
        });
    };

    const updateEnable = async (record: any, enable: boolean) => {
        const result: any = await jsonRequest.put("/back/health-consultation-config/modify", {
            ...record,
            enable,
        }).then((resp: any) => resp.json());
        if (result?.code === 0) {
            message.success(enable ? "已开启" : "已关闭");
            tableRef.current?.reload();
            return;
        }
        message.error(result?.msg || "更新失败");
    };

    const removeConfig = async (id: string) => {
        const result: any = await jsonRequest.delete(`/back/health-consultation-config/delete/${id}`, {})
            .then((resp: any) => resp.json());
        if (result?.code === 0) {
            message.success("删除成功");
            tableRef.current?.reload();
            return;
        }
        message.error(result?.msg || "删除失败");
    };

    const columns: any[] = [
        {
            title: "渠道",
            dataIndex: "channel",
            align: "center",
        },
        {
            title: "是否开启",
            dataIndex: "enable",
            align: "center",
            renderFormItem: () => (
                <Select
                    allowClear
                    placeholder="请选择状态"
                    options={[
                        { label: "开启", value: true },
                        { label: "关闭", value: false },
                    ]}
                />
            ),
            render: (value: boolean, record: any) => (
                <Switch
                    checked={Boolean(value)}
                    checkedChildren="开启"
                    unCheckedChildren="关闭"
                    onChange={(enable) => updateEnable(record, enable)}
                />
            ),
        },
        {
            title: "微信号",
            dataIndex: "wechatId",
            align: "center",
            search: false,
        },
        {
            title: "沉默时长",
            dataIndex: "silence",
            align: "center",
            search: false,
            render: (value: number) => `${value ?? 0} 小时`,
        },
        {
            title: "企业获客助手链接",
            dataIndex: "dialogWorkUrl",
            align: "center",
            search: false,
            ellipsis: true,
        },
        {
            title: "更新时间",
            dataIndex: "updateAt",
            align: "center",
            search: false,
        },
        {
            title: "操作",
            key: "operation",
            align: "center",
            search: false,
            render: (_: any, record: any) => (
                <>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => openConfigModal(record)}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确认删除该健康咨询配置？"
                        okText="删除"
                        cancelText="取消"
                        onConfirm={() => removeConfig(record.id)}
                    >
                        <Button type="link" danger icon={<DeleteOutlined />}>
                            删除
                        </Button>
                    </Popconfirm>
                </>
            ),
        },
    ];

    return (
        <>
            <h1>健康咨询配置</h1>
            <ProTable
                rowKey="id"
                actionRef={tableRef}
                columns={columns}
                request={request}
                cardBordered
                pagination={{ defaultPageSize: 10, showSizeChanger: true }}
                search={{ span: 6, defaultCollapsed: false }}
                toolBarRender={() => [
                    <Button
                        key="add"
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => openConfigModal({})}
                    >
                        新增
                    </Button>,
                ]}
            />
        </>
    );
}
